import { useLocation } from "react-router-dom"
import QueryString from 'query-string'
import constants from "../Constants"
import { NavSection, Route } from "../components/NavView"
import { useIsAdmin, userState, useUser } from "../atoms"
import { selector, useRecoilValue } from "recoil"
import { useState } from "react"
import { serviceEditMode } from "../atoms/serviceDescriptor"
import { groupDescriptorsState } from "../atoms/portale"
import { AffariGeneraliIconComponent, AssociativiIconComponent, BugReportIconComponent, 
    CrmIconComponent, FactoryIconComponent, FiscaleIconComponent, ManageAccountsIconComponent, 
    PagheIconComponent, ServiziComuniIconComponent, TecnologicaIconComponent } from "./iconsOfRoutes"

export const useLocationBase = () => {
    const location = useLocation()
    const temp = location.pathname.split('/')
    const base = temp.length <= 2 ? 'do' : `do/${temp[2]}`

    return base
}
export const useIsLocalhost = () => {
    const host = window.location.host
    return host.startsWith("localhost")
}

export const useQueryString = () => {
    const location = useLocation()
    return QueryString.parse(location.search)
}

export type AmbientInfoType = {
    color: string,
    label: string,
    labelColor: string,
}
export const useAmbientInfo = () => {
    // Per l'ambiente di test vogliamo il banner
    const isTest = window.location.host.split(':')[0].split('.')[0].toLowerCase().endsWith('test')
    return {
        color: constants.ambColor,
        label: isTest ? 'TEST' : constants.ambiente,
        labelColor: constants.ambLabelColor,
    }
}

const isArea = (aree: string[] | null, area: string) => aree?.find(x => x === area)

export const routesAreeState = selector({
    key: 'routesAreeState',
    get: ({ get }) => {
        const sdEditMode = get(serviceEditMode)
        const user = get(userState)
        const isAzienda = user?.role === 'azienda'
        const groups = get(groupDescriptorsState)
        const allAree = groups?.map(g => g.areaUam)
        const routes: Route[] = []
        const aree = sdEditMode === 'edit' ? allAree : user?.context?.aree
        if (aree && aree.length > 0) {
            if (isArea(aree, 'fiscale')) routes.push({ to: `/do/fiscale`, label: 'Fiscale', icon: FiscaleIconComponent })
            if (isArea(aree, 'paghe')) routes.push({ to: `/do/paghe`, label: 'Paghe', icon: PagheIconComponent })
            if (isArea(aree, 'affarigen')) routes.push({ to: `/do/affgen`, label: 'Affari Generali', icon: AffariGeneraliIconComponent })
            if (isArea(aree, 'servcom')) {
                routes.push({ to: `/do/servcom`, label: 'Servizi Comuni', icon: ServiziComuniIconComponent })
                routes.push({ to: `/do/crm`, label: 'CRM', icon: CrmIconComponent })
            }
            if (isArea(aree, 'associativi')) routes.push({ to: `/do/assoc`, label: 'Associativi', icon: AssociativiIconComponent })
            if (isArea(aree, 'tecnologica')) routes.push({ to: `/do/tecn`, label: 'Tecnologici', icon: TecnologicaIconComponent })
            if (isArea(aree, 'azienda')) routes.push({ to: `/do/azienda`, label: 'Aziendale', icon: FactoryIconComponent })
        }
        if (isAzienda) {
            routes.push({ to: `/do/azienda`, label: 'Attività' })
        }
        return routes
    }
})

const convertAreeDic: Record<string, string[]> = {
    "paghe": ["paghe"],
    "fiscale": ["fiscale"],
    "informativa": ["informativa"],
    "servcom": ["servcom", "crm"],
    "tecnologica": ["tecn"],
    "informadmin": ["informadmin"],
    "associativi": ["assoc"],
    "affarigen": ["affgen"],
    "stampepub": ["stampepub"],
    "docpub": ["docpub"],
    "hd": ["hd"],
}

export const areeMySixtemaState = selector({
    key: 'areeMySixtemaState',
    get: ({ get }) => {
        const user = get(userState)
        if (user) {
            const areeFromContext = user.context?.aree
            if (areeFromContext) {
                return areeFromContext.reduce<string[]>((p, a) => p.concat(convertAreeDic[a]), [])
            }
        }
        return []
    }
})

export const useAree = () => {
    const areeArray = useRecoilValue(areeMySixtemaState)
    const aree = areeArray.join(';') + ';'
    return { aree, areeArray }
}

const isThereSomethingLikeAmministrazione = true

export const useNavSections = () => {
    const isLocalhost = useIsLocalhost()
    const routesAree = useRecoilValue(routesAreeState)
    const navSections: NavSection[] = [];
    const user = useUser()
    const isAdmin = useIsAdmin()
    // Load the nav sections
    if (user?.context) {
        // Rotte delle aree
        navSections.push({
            title: 'Aree',
            routes: routesAree
        })
        // Rotte per utente Admin
        if (isAdmin && isThereSomethingLikeAmministrazione) {
            navSections.push({
                title: 'Amministrazione',
                routes: [
                    //{ to: "adm/gestserv", label: "Gestione servizi", icon: ManageAccountsIconComponent },
                    { to: "adm/carousel", label: "Gestione carousel", icon: ManageAccountsIconComponent }
                    //{ to: "test/search", label: "Test Search" }
                ]
            })
        }
        if (isLocalhost && (user.name.startsWith('SIATURRINI') || user.name.startsWith('SIAMORELL'))) {
            navSections.push({
                title: 'Test Vari',
                routes: [
                    { to: "adm/servicedescriptor", label: "Gestione servizi", icon: ManageAccountsIconComponent },
                    //{ to: "adm/service", label: "Gestione servizi", icon: ManageAccountsIconComponent },
                    { to: "test/search", label: "Test Search", icon: BugReportIconComponent }
                ]
            })
        }
    }
    return navSections;

}

// Hook
export function useLocalStorage<T>(key: string, initialValue: T) {
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState(() => {
        try {
            // Get from local storage by key
            const item = window.localStorage.getItem(key);
            // console.log(`useLocalStorage.getItem: ${key} => ${item}`)
            // Parse stored json or if none return initialValue
            return item ? JSON.parse(item) as T : initialValue;
        } catch (error) {
            // If error also return initialValue
            console.log(error);
            return initialValue;
        }
    });
    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    const setValue = (value: T) => {
        try {
            // Allow value to be a function so we have same API as useState
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            // Save state
            setStoredValue(valueToStore);
            // Save to local storage
            const serialized = JSON.stringify(valueToStore)
            // console.log(`useLocalStorage.setItem: ${key} => ${serialized}`)
            window.localStorage.setItem(key, serialized);
        } catch (error) {
            // A more advanced implementation would handle the error case
            console.log(error);
        }
    };
    return [storedValue, setValue] as [T, (value: T) => void];
}